import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Upload, X, Loader2, ShieldCheck } from "lucide-react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { useI18n } from "../../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fieldBase =
  "w-full bg-transparent border-b border-border py-3 text-primary placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none transition-colors font-sans text-[15px]";

const FieldLabel = ({ children, htmlFor, required }) => (
  <label htmlFor={htmlFor} className="block text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 font-sans">
    {children}{required && <span className="text-accent ml-1">*</span>}
  </label>
);

export default function CaseReviewForm({ defaultType = "" }) {
  const { t, lang } = useI18n();
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    full_name: "", country: "", email: "", phone: "",
    preferred_contact: "", procedure_category: "", procedure_type: defaultType,
    preferred_month: "", message: "", consent: false,
  });

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    ["full_name", "country", "email", "phone", "preferred_contact", "procedure_category", "procedure_type"].forEach((k) => {
      if (!form[k]) e[k] = t("form.errors.required");
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("form.errors.email");
    if (!form.consent) e.consent = t("form.errors.consent");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files.slice(0, 8 - photos.length)) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name}: unsupported file type`);
          continue;
        }
        if (file.size > 12 * 1024 * 1024) {
          toast.error(`${file.name}: file too large (max 12 MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, res.data]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("form.errors.submit"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(t("form.errors.submit"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/case-reviews`, {
        ...form,
        photo_ids: photos.map((p) => p.id),
        language: lang,
      });
      setDone(true);
      toast.success(t("form.success.heading"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("form.errors.submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ full_name: "", country: "", email: "", phone: "", preferred_contact: "", procedure_category: "", procedure_type: defaultType, preferred_month: "", message: "", consent: false });
    setPhotos([]);
    setDone(false);
    setErrors({});
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="bg-surface border border-border rounded-lg p-8 md:p-12" data-testid="case-review-success"
      >
        <div className="w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center mb-6">
          <Check className="w-7 h-7 text-accent" strokeWidth={1.75} />
        </div>
        <h3 className="font-serif text-3xl md:text-4xl text-primary mb-6">{t("form.success.heading")}</h3>
        <ul className="space-y-3 mb-8">
          {t("form.success.points").map((p, i) => (
            <li key={i} className="flex gap-3 text-[15px] text-secondary-foreground">
              <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <button onClick={reset} className="text-accent font-medium hover:underline underline-offset-4 font-sans" data-testid="submit-again-btn">
          {t("form.success.again")}
        </button>
      </motion.div>
    );
  }

  const cats = t("form.categoryOptions");
  const types = t("form.typeOptions");
  const contacts = t("form.contactOptions");

  return (
    <form onSubmit={onSubmit} className="bg-surface border border-border rounded-lg p-6 md:p-10" data-testid="case-review-form" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
        <div>
          <FieldLabel htmlFor="cr-name" required>{t("form.fullName")}</FieldLabel>
          <input id="cr-name" className={fieldBase} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="cr-full-name" />
          {errors.full_name && <p className="text-destructive text-xs mt-1">{errors.full_name}</p>}
        </div>
        <div>
          <FieldLabel htmlFor="cr-country" required>{t("form.country")}</FieldLabel>
          <input id="cr-country" className={fieldBase} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="cr-country" />
          {errors.country && <p className="text-destructive text-xs mt-1">{errors.country}</p>}
        </div>
        <div>
          <FieldLabel htmlFor="cr-email" required>{t("form.email")}</FieldLabel>
          <input id="cr-email" type="email" className={fieldBase} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="cr-email" />
          {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
        </div>
        <div>
          <FieldLabel htmlFor="cr-phone" required>{t("form.phone")}</FieldLabel>
          <input id="cr-phone" className={fieldBase} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="cr-phone" />
          {errors.phone && <p className="text-destructive text-xs mt-1">{errors.phone}</p>}
        </div>

        <div>
          <FieldLabel required>{t("form.preferredContact")}</FieldLabel>
          <Select value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
            <SelectTrigger className="border-0 border-b border-border rounded-none bg-transparent px-0 focus:ring-0" data-testid="cr-preferred-contact">
              <SelectValue placeholder={t("form.placeholders.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">{contacts.email}</SelectItem>
              <SelectItem value="phone">{contacts.phone}</SelectItem>
              <SelectItem value="whatsapp">{contacts.whatsapp}</SelectItem>
            </SelectContent>
          </Select>
          {errors.preferred_contact && <p className="text-destructive text-xs mt-1">{errors.preferred_contact}</p>}
        </div>
        <div>
          <FieldLabel required>{t("form.procedureCategory")}</FieldLabel>
          <Select value={form.procedure_category} onValueChange={(v) => set("procedure_category", v)}>
            <SelectTrigger className="border-0 border-b border-border rounded-none bg-transparent px-0 focus:ring-0" data-testid="cr-procedure-category">
              <SelectValue placeholder={t("form.placeholders.select")} />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(cats).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          {errors.procedure_category && <p className="text-destructive text-xs mt-1">{errors.procedure_category}</p>}
        </div>

        <div className="md:col-span-2">
          <FieldLabel required>{t("form.procedureType")}</FieldLabel>
          <Select value={form.procedure_type} onValueChange={(v) => set("procedure_type", v)}>
            <SelectTrigger className="border-0 border-b border-border rounded-none bg-transparent px-0 focus:ring-0" data-testid="cr-procedure-type">
              <SelectValue placeholder={t("form.placeholders.select")} />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(types).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          {errors.procedure_type && <p className="text-destructive text-xs mt-1">{errors.procedure_type}</p>}
        </div>

        <div>
          <FieldLabel htmlFor="cr-month">{t("form.preferredMonth")}</FieldLabel>
          <input id="cr-month" className={fieldBase} placeholder={t("form.placeholders.month")} value={form.preferred_month} onChange={(e) => set("preferred_month", e.target.value)} data-testid="cr-month" />
        </div>
        <div className="flex items-end">
          <div className="w-full">
            <FieldLabel>{t("form.photoUpload")}</FieldLabel>
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-2 border-b border-dashed border-border py-3 text-muted-foreground hover:text-accent hover:border-accent transition-colors font-sans text-sm" data-testid="cr-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {t("form.photoUpload")}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="cr-file-input" />
          </div>
        </div>

        <div className="md:col-span-2">
          <FieldLabel htmlFor="cr-message">{t("form.message")}</FieldLabel>
          <textarea id="cr-message" rows={3} className={`${fieldBase} resize-none`} placeholder={t("form.placeholders.message")} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="cr-message" />
        </div>

        {photos.length > 0 && (
          <div className="md:col-span-2 flex flex-wrap gap-3" data-testid="cr-photo-previews">
            {photos.map((p) => (
              <div key={p.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-border group">
                <img src={`${BACKEND_URL}${p.url}`} alt="Uploaded preview" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(p.id)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove photo">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="md:col-span-2 text-xs text-muted-foreground -mt-2">{t("form.photoHint")}</p>

        <div className="md:col-span-2 flex items-start gap-3 pt-2">
          <Checkbox id="cr-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-1" data-testid="cr-consent" />
          <label htmlFor="cr-consent" className="text-[13px] text-secondary-foreground leading-relaxed cursor-pointer font-sans">
            {t("form.consent")}
          </label>
        </div>
        {errors.consent && <p className="md:col-span-2 text-destructive text-xs -mt-3">{errors.consent}</p>}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
        <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 font-medium font-sans whitespace-nowrap hover:bg-primary/90 transition-all active:scale-[0.97] disabled:opacity-60" data-testid="submit-case-review-btn">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {t("cta.submitReview")}
        </button>
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-accent" /> {t("form.disclaimer")}
        </p>
      </div>
    </form>
  );
}
